// Ein Foto groß (Mockup /doku/foto/:nr/:i): das ganze Foto mit seiner
// Zeichnung, darunter der Streifen mit allen Fotos der Station, „Auf dem
// Foto zeichnen“, die Notiz nur zu diesem Foto – getippt, mit dem Mikrofon
// der Tastatur diktiert oder (seit Schritt 3) als Sprachmemo eingesprochen –
// und „Foto löschen“ mit Rückfrage. Unten fest: „Fertig“ zurück zur Station;
// oben links steht, wohin der Pfeil führt: „‹ Station 3“ (seit 19.10.2026).
//
// Seit Schritt 4, gesendet: nur zum Ansehen – das Foto groß mit Zeichnung,
// der Streifen, die Notiz als Text, die Memos zum Abspielen; kein Zeichnen,
// kein Löschen. Liegt das Foto nicht mehr am Gerät, kommt es aus dem Büro.
//
// Seit Schritt 5, im Nachtrag: Ein Foto, das mit ihm dazukam, lässt sich
// ganz bearbeiten (zeichnen, Notiz, Memos, löschen); ein schon gesendetes
// nur ansehen – mit dem Hinweis, wo Nachzureichendes hingehört.
//
// Seit E3 (S6), wenn die Besichtigung gesendet ist: gleich unter dem Foto
// die Fragen an ihm, unter seiner Notiz die Fragen an ihr, unten „Frage
// stellen“ zu diesem Foto – sobald es beim Büro ist.
//
// Adresse: #/aufnahme/<kennung>/station/<nr>/foto/<foto-kennung>

import { useEffect, useRef, useState } from "react";
import { AUFNAHME_GRENZEN, type IchAntwort } from "@happy-energy/kern";
import { AppFuss, AppKopf, Ikon, Tipp, toastZeigen } from "@happy-energy/oberflaeche";
import { fotoLoeschen, fotoNotizSpeichern, type Bearbeiten } from "../arbeitskopie.ts";
import type { Foto, Station } from "../speicher.ts";
import { useMemoAufnahme } from "../memo.ts";
import { fragenAmGeraet, gespraechMoeglich } from "../gespraech.ts";
import { aufnahmeLesen, memosZurNotiz, modusVon, type AufnahmeGanz } from "./Uebersicht.tsx";
import { LeseText, SendeStand, speichernFehlgeschlagen, useLive, usePostausgang, useSpeichern } from "./gemeinsam.tsx";
import { FotoBild, gezeichnet, sendungsMarke } from "./fotos.tsx";
import { MemoListe, NotizFuss } from "./memos.tsx";
import { FragenAbschnitt, FrageStellen, offeneAn } from "./fragen.tsx";

/** Wo eine Station und ihre Fotos zu finden sind. */
export function stationsAdresse(aufnahmeId: string, nr: number): string {
  return `#/aufnahme/${aufnahmeId}/station/${nr}`;
}

export function fotoAdresse(aufnahmeId: string, nr: number, fotoId: string): string {
  return `${stationsAdresse(aufnahmeId, nr)}/foto/${fotoId}`;
}

/**
 * Lässt sich dieses Foto bearbeiten? Beim Aufnehmen jedes, im Nachtrag nur
 * eines, das mit ihm dazukam (wie in der Arbeitskopie) – sonst nur ansehen.
 */
export function fotoBearbeitbar(foto: Pick<Foto, "nachtragId">, modus: Bearbeiten): boolean {
  return modus.art === "aufnahme" || (modus.art === "nachtrag" && foto.nachtragId === modus.nachtragId);
}

/** Hat das Foto eine Notiz – mit Text oder mit Sprachmemo? */
function hatNotiz(ganz: AufnahmeGanz, fotoId: string): boolean {
  return ganz.notizen.some((n) => n.fotoId === fotoId && (n.text.trim() !== "" || memosZurNotiz(ganz, n).length > 0));
}

function FotoInhalt({
  ich,
  ganz,
  station,
  fotos,
  foto,
}: {
  ich: IchAntwort;
  ganz: AufnahmeGanz;
  station: Station;
  fotos: Foto[];
  foto: Foto;
}) {
  const nr = fotos.indexOf(foto) + 1;
  // Gesendet: nur zum Ansehen – im Nachtrag lässt sich bearbeiten, was mit ihm dazukam.
  const modus = modusVon(ganz);
  const nurLesen = !fotoBearbeitbar(foto, modus);
  const post = usePostausgang();
  const zurStation = stationsAdresse(ganz.aufnahme.id, station.nr);
  const [notiz, setNotiz] = useState(() => ganz.notizen.find((n) => n.fotoId === foto.id)?.text ?? "");
  const [gespeichert, setGespeichert] = useState(false);
  const [rueckfrage, setRueckfrage] = useState(false);
  const rueckfrageKasten = useRef<HTMLDivElement>(null);
  // Die Rückfrage ganz zeigen – sonst lägen ihre Knöpfe unter dem festen Fuß.
  useEffect(() => {
    if (rueckfrage) rueckfrageKasten.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [rueckfrage]);
  const notizSpeicher = useSpeichern(async (text: string) => {
    await fotoNotizSpeichern(foto, text);
    setGespeichert(true);
  });
  const notizFeld = useRef<HTMLTextAreaElement>(null);
  const memos = memosZurNotiz(ganz, ganz.notizen.find((n) => n.fotoId === foto.id));
  // Während einer Aufnahme an diesem Foto lässt es sich nicht löschen.
  const aufnahme = useMemoAufnahme();
  const nimmtAuf = aufnahme.art !== "aus" && aufnahme.ort === foto.id;

  async function loeschen() {
    // Was noch nicht gespeichert ist, gehört zu einem Foto, das es gleich nicht mehr gibt.
    notizSpeicher.verwerfen();
    window.location.hash = zurStation;
    try {
      await fotoLoeschen(foto);
      toastZeigen("Foto gelöscht");
    } catch (fehler) {
      speichernFehlgeschlagen(fehler, "Konnte das Foto nicht löschen – bitte noch einmal.");
    }
  }

  const fotoNotiz = ganz.notizen.find((n) => n.fotoId === foto.id);

  // Fragen (seit E3): erst, wenn die Besichtigung gesendet ist; fragen geht an einem Foto, das beim Büro ist
  // (aus der ersten Sendung oder einem Nachtrag, der im Büro ist) – an einem, das noch kommt, gäbe es nichts zu fragen.
  const ichKurz = { id: ich.nutzer.id, name: ich.nutzer.name };
  const gesendet = gespraechMoeglich(ganz.aufnahme);
  const fragen = gesendet ? fragenAmGeraet(ganz.aufnahme) : [];
  const amFoto = fragen.filter((f) => f.bezug.tabelle === "foto" && f.bezug.id === foto.id);
  const anDerNotiz = fotoNotiz ? fragen.filter((f) => f.bezug.tabelle === "notiz" && f.bezug.id === fotoNotiz.id) : [];
  const beimBuero = foto.nachtragId === null || ganz.nachtraege.some((n) => n.id === foto.nachtragId && n.stand === "im_buero");
  const notizIdJeFoto = new Map(ganz.notizen.flatMap((n) => (n.fotoId !== null ? [[n.fotoId, n.id] as const] : [])));
  const mitFrage = (f: Foto) =>
    offeneAn(fragen, (x) => (x.bezug.tabelle === "foto" && x.bezug.id === f.id) || (x.bezug.tabelle === "notiz" && x.bezug.id === notizIdJeFoto.get(f.id))) > 0;

  return (
    <div className="app-seite">
      <AppKopf
        umgebung={ich.umgebung}
        zurueck={{ adresse: zurStation, name: `Station ${station.nr}` }}
        titel={`Foto ${nr} von ${fotos.length}`}
        // Was noch ans Büro muss (seit 19.10.2026) – dass gezeichnet ist, sieht man am Foto und am Knopf „Weiter zeichnen“.
        rechts={<SendeStand wartend={post.wartend} />}
      />
      <main className="app-inhalt">
        <FotoBild foto={foto} art="ganz" alt={`Foto ${nr}`} />

        {/* Die Fragen an diesem Foto – gleich darunter, groß (seit E3). */}
        <FragenAbschnitt fragen={amFoto} ich={ichKurz} aufnahmeId={ganz.aufnahme.id} />

        {fotos.length > 1 && (
          <nav className="foto-streifen" aria-label="Alle Fotos dieser Station">
            {fotos.map((f, i) => (
              <a
                key={f.id}
                href={fotoAdresse(ganz.aufnahme.id, station.nr, f.id)}
                className={f.id === foto.id ? "aktiv" : undefined}
                aria-current={f.id === foto.id ? "page" : undefined}
              >
                <FotoBild
                  foto={f}
                  art="kachel"
                  notiz={hatNotiz(ganz, f.id)}
                  marke={sendungsMarke(f, modus)}
                  frage={mitFrage(f)}
                  alt={`Foto ${i + 1}`}
                />
              </a>
            ))}
          </nav>
        )}

        {!nurLesen && (
          <a href={`${fotoAdresse(ganz.aufnahme.id, station.nr, foto.id)}/zeichnen`} className="knopf-dunkel knopf-block">
            <Ikon name="stift" /> {gezeichnet(foto) ? "Weiter zeichnen" : "Auf dem Foto zeichnen"}
          </a>
        )}

        <div className="abschnitt-titel" id="foto-notiz-titel">
          Notiz zu diesem Foto
        </div>
        {nurLesen ? (
          <div className="text-karte nur-lesen">
            {/* Nur eingesprochen: dann stehen nur die Memos da. */}
            {(fotoNotiz?.text.trim() || memos.length === 0) && <LeseText text={fotoNotiz?.text} leer="Keine Notiz zu diesem Foto." />}
            <MemoListe memos={memos} nurLesen />
          </div>
        ) : (
          // Ein Tipp neben das Feld setzt den Cursor hinein – wie beim Feld selbst.
          <div
            className="text-karte"
            onClick={(e) => {
              if (e.target === e.currentTarget) notizFeld.current?.focus();
            }}
          >
            <textarea
              ref={notizFeld}
              id="foto-notiz"
              aria-labelledby="foto-notiz-titel"
              rows={3}
              maxLength={AUFNAHME_GRENZEN.text}
              value={notiz}
              placeholder="Was ist auf diesem Foto wichtig? Tippen oder diktieren"
              onChange={(e) => {
                setNotiz(e.target.value);
                setGespeichert(false);
                notizSpeicher.spaeter(e.target.value);
              }}
              onBlur={notizSpeicher.sofort}
            />
            <MemoListe memos={memos} />
            <NotizFuss
              ort={{ aufnahmeId: ganz.aufnahme.id, stationId: null, fotoId: foto.id }}
              vorher={notizSpeicher.sofort}
              gespeichert={gespeichert}
            />
          </div>
        )}
        {/* Die Fragen an der Notiz dieses Fotos (seit E3). */}
        <FragenAbschnitt fragen={anDerNotiz} ich={ichKurz} aufnahmeId={ganz.aufnahme.id} />
        {!nurLesen && <Tipp art="grau">Diese Notiz hängt nur an diesem Foto. Für die ganze Station gibt es die Notiz an der Station.</Tipp>}
        {nurLesen && modus.art === "nachtrag" && (
          <Tipp art="grau">
            Dieses Foto ist schon im Büro – es bleibt, wie es ist. Etwas dazu? Schreib es an der Station in den Nachtrag zur Notiz, oder mach ein
            neues Foto.
          </Tipp>
        )}
        {gesendet && beimBuero && (
          <FrageStellen aufnahmeId={ganz.aufnahme.id} bezug={{ tabelle: "foto", id: foto.id }} ich={ichKurz} wo="zu diesem Foto" />
        )}

        {nimmtAuf || nurLesen ? null : rueckfrage ? (
          <div ref={rueckfrageKasten} className="rueckfrage" role="alertdialog" aria-labelledby="rueckfrage-titel">
            <b id="rueckfrage-titel">Dieses Foto löschen?</b>
            <span>Zeichnung, Notiz und Sprachmemos dazu gehen mit. Das lässt sich nicht rückgängig machen.</span>
            <div className="knopf-reihe">
              <button type="button" className="knopf-rand" onClick={() => setRueckfrage(false)}>
                Behalten
              </button>
              <button type="button" className="knopf-loeschen" onClick={() => void loeschen()}>
                <Ikon name="muell" /> Löschen
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="knopf-leise-rot" onClick={() => setRueckfrage(true)}>
            <Ikon name="muell" /> Foto löschen
          </button>
        )}
      </main>
      <AppFuss>
        <a href={zurStation} className="knopf-haupt knopf-block" onClick={notizSpeicher.sofort}>
          <Ikon name="haken" /> Fertig
        </a>
      </AppFuss>
    </div>
  );
}

/** Lädt Besichtigung, Station und Foto – und sagt freundlich, wenn es sie hier nicht gibt. */
export function useFotoDaten(aufnahmeId: string, nr: number, fotoId: string) {
  const ganz = useLive(() => aufnahmeLesen(aufnahmeId), aufnahmeId);
  if (ganz === undefined) return undefined;
  const station = ganz?.stationen.find((s) => s.nr === nr);
  if (!ganz || !station) return null;
  const fotos = ganz.fotos.filter((f) => f.stationId === station.id);
  const foto = fotos.find((f) => f.id === fotoId);
  return foto ? { ganz, station, fotos, foto } : null;
}

/** Das Foto gibt es (hier) nicht – etwa gerade gelöscht oder auf einem anderen Gerät aufgenommen. */
export function FotoFehlt({ ich, aufnahmeId, nr }: { ich: IchAntwort; aufnahmeId: string; nr: number }) {
  return (
    <div className="app-seite">
      <AppKopf
        umgebung={ich.umgebung}
        zurueck={{ adresse: stationsAdresse(aufnahmeId, nr), name: `Station ${nr}` }}
        titel="Foto"
        gross="Nicht gefunden"
      />
      <main className="app-inhalt">
        <Tipp art="grau">Dieses Foto ist auf diesem Gerät nicht (mehr) gespeichert.</Tipp>
      </main>
    </div>
  );
}

export function FotoSeite({ ich, aufnahmeId, nr, fotoId }: { ich: IchAntwort; aufnahmeId: string; nr: number; fotoId: string }) {
  const daten = useFotoDaten(aufnahmeId, nr, fotoId);
  if (daten === undefined) return <div className="app-seite" />;
  if (daten === null) return <FotoFehlt ich={ich} aufnahmeId={aufnahmeId} nr={nr} />;
  // Je Foto ein eigenes Formular: Beim Wechsel im Streifen wird gespeichert, was offen ist, und neu gelesen.
  return <FotoInhalt key={daten.foto.id} ich={ich} {...daten} />;
}
